export const toAmountNumber = (value) => {
  const numeric = Number.parseFloat(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

export const ALL_PLAN_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const derivePlanMonths = ({
  planScope = "full",
  planHalf = "H1",
  planQuarter = "Q1",
  months,
} = {}) => {
  if (Array.isArray(months) && months.length) {
    return [...months];
  }

  if (planScope === "half") {
    return planHalf === "H2"
      ? ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
      : ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
  }

  if (planScope === "quarter") {
    if (planQuarter === "Q2") return ["Apr", "May", "Jun"];
    if (planQuarter === "Q3") return ["Jul", "Aug", "Sep"];
    if (planQuarter === "Q4") return ["Oct", "Nov", "Dec"];
    return ["Jan", "Feb", "Mar"];
  }

  return [...ALL_PLAN_MONTHS];
};

export const sumCategoriesByMonth = (categories = [], months = []) =>
  months.reduce((acc, month) => {
    acc[month] = (categories || []).reduce(
      (sum, category) => sum + toAmountNumber(category?.amounts?.[month]),
      0
    );
    return acc;
  }, {});

export const getPlanDateRange = ({
  planYear,
  planScope = "full",
  planHalf = "H1",
  planQuarter = "Q1",
} = {}) => {
  const year = String(planYear || "").trim();
  if (!year) return { startDate: "", endDate: "" };

  if (planScope === "half") {
    return planHalf === "H2"
      ? { startDate: `${year}-07`, endDate: `${year}-12` }
      : { startDate: `${year}-01`, endDate: `${year}-06` };
  }

  if (planScope === "quarter") {
    if (planQuarter === "Q2") return { startDate: `${year}-04`, endDate: `${year}-06` };
    if (planQuarter === "Q3") return { startDate: `${year}-07`, endDate: `${year}-09` };
    if (planQuarter === "Q4") return { startDate: `${year}-10`, endDate: `${year}-12` };
    return { startDate: `${year}-01`, endDate: `${year}-03` };
  }

  return { startDate: `${year}-01`, endDate: `${year}-12` };
};

export const normalizeOpeningBalance = (value, { allowEmpty = true } = {}) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return allowEmpty ? "" : "0.00";
  }

  const numeric = Number.parseFloat(String(value).replace(/[^\d.-]/g, ""));
  if (!Number.isFinite(numeric)) {
    return allowEmpty ? "" : "0.00";
  }

  return Math.max(0, numeric).toFixed(2);
};

export const buildCumulativeBalanceByMonth = ({
  months = [],
  openingBalance = 0,
  incomeTotals = {},
  savingTotals = {},
  expenseTotals = {},
} = {}) => {
  let runningBalance = toAmountNumber(openingBalance);

  return months.reduce((acc, month) => {
    runningBalance += toAmountNumber(incomeTotals?.[month]);
    runningBalance -= toAmountNumber(savingTotals?.[month]);
    runningBalance -= toAmountNumber(expenseTotals?.[month]);
    acc[month] = runningBalance;
    return acc;
  }, {});
};

export const buildFinancialPlanBalanceSnapshots = ({
  months = [],
  openingBalance = 0,
  incomeTotals = {},
  savingTotals = {},
  expenseTotals = {},
} = {}) => {
  const incomeCumulativeBalanceTotals = buildCumulativeBalanceByMonth({
    months,
    openingBalance,
    incomeTotals,
  });

  const savingsCumulativeBalanceTotals = buildCumulativeBalanceByMonth({
    months,
    openingBalance,
    incomeTotals,
    savingTotals,
  });

  const expenseCumulativeBalanceTotals = buildCumulativeBalanceByMonth({
    months,
    openingBalance,
    incomeTotals,
    savingTotals,
    expenseTotals,
  });

  return {
    incomeCumulativeBalanceTotals,
    savingsCumulativeBalanceTotals,
    expenseCumulativeBalanceTotals,
    cumulativeBalanceTotals: expenseCumulativeBalanceTotals,
  };
};

export const buildMonthlyBalanceAfterExpenses = ({
  months = [],
  incomeTotals = {},
  savingTotals = {},
  expenseTotals = {},
} = {}) =>
  months.reduce((acc, month) => {
    acc[month] =
      toAmountNumber(incomeTotals?.[month]) -
      toAmountNumber(savingTotals?.[month]) -
      toAmountNumber(expenseTotals?.[month]);
    return acc;
  }, {});

/**
 * Cumulative + safety-clamped savings allocator.
 *
 * Goals:
 *   1. The plan's net cash should equal `endBuffer` at end-of-period (balance-to-zero by default).
 *   2. No month's running cash should drop below `monthlyBufferFloor`.
 *   3. The surplus is distributed evenly (with carry-forward) so each month gets a realistic, habit-friendly contribution.
 *   4. A monthly savings rate above `maxSavingsRate` of that month's income emits a warning (does not block).
 *
 * Block conditions (status ≠ "balanced"):
 *   - "deficit"  — pre-sweep cumulative cash dips below the floor in some month.
 *   - "shortfall" — totalToSweep < 0 (income + opening − expenses − explicit savings < endBuffer).
 *
 * Inputs that already exist in the plan (income, explicit savings, expenses) are not
 * altered by this function; the returned `sweepAmounts` are *additional* savings to
 * add to one chosen savings category.
 */
export const analyzePlanSavings = ({
  months = [],
  openingBalance = 0,
  incomeTotals = {},
  savingTotals = {},
  expenseTotals = {},
  endBuffer = 0,
  monthlyBufferFloor = 0,
  maxSavingsRate = 0.5,
} = {}) => {
  const ob = toAmountNumber(openingBalance);
  const eb = toAmountNumber(endBuffer);
  const floor = toAmountNumber(monthlyBufferFloor);
  const rateCap = Math.max(0, Math.min(1, Number(maxSavingsRate) || 0));

  const safeMonths = Array.isArray(months) ? months : [];
  const N = safeMonths.length;

  const preSweepCumulativeCash = {};
  let cash = ob;
  let totalIncome = 0;
  let totalExpense = 0;
  let totalExplicitSavings = 0;
  let firstDeficitMonth = null;
  let firstDeficitAmount = 0;

  for (const m of safeMonths) {
    const inc = toAmountNumber(incomeTotals?.[m]);
    const sav = toAmountNumber(savingTotals?.[m]);
    const exp = toAmountNumber(expenseTotals?.[m]);
    cash += inc - sav - exp;
    preSweepCumulativeCash[m] = cash;
    totalIncome += inc;
    totalExpense += exp;
    totalExplicitSavings += sav;
    if (cash + 1e-6 < floor && firstDeficitMonth === null) {
      firstDeficitMonth = m;
      firstDeficitAmount = floor - cash;
    }
  }

  const totalToSweep = ob + totalIncome - totalExpense - totalExplicitSavings - eb;

  const zeroSweep = Object.fromEntries(safeMonths.map((m) => [m, 0]));

  if (firstDeficitMonth !== null) {
    return {
      feasibility: {
        status: "deficit",
        shortfall: 0,
        deficitMonth: firstDeficitMonth,
        deficitAmount: firstDeficitAmount,
        message:
          `Cash dips ${firstDeficitAmount.toFixed(2)} below the buffer floor in ${firstDeficitMonth}. ` +
          `Reduce expenses, increase income, or lower planned savings before ${firstDeficitMonth}.`,
      },
      totalToSweep: Math.max(0, totalToSweep),
      sweepAmounts: zeroSweep,
      warnings: [],
      projection: {
        preSweepCumulativeCash,
        postSweepCumulativeCash: { ...preSweepCumulativeCash },
      },
    };
  }

  if (totalToSweep < -1e-6) {
    return {
      feasibility: {
        status: "shortfall",
        shortfall: -totalToSweep,
        deficitMonth: null,
        deficitAmount: 0,
        message:
          `Plan is short by ${(-totalToSweep).toFixed(2)} over the period. ` +
          `Reduce expenses, increase income, lower planned savings, or lower your end-of-period buffer.`,
      },
      totalToSweep: 0,
      sweepAmounts: zeroSweep,
      warnings: [],
      projection: {
        preSweepCumulativeCash,
        postSweepCumulativeCash: { ...preSweepCumulativeCash },
      },
    };
  }

  if (N === 0 || totalToSweep <= 1e-6) {
    return {
      feasibility: {
        status: "balanced",
        shortfall: 0,
        deficitMonth: null,
        deficitAmount: 0,
        message: "",
      },
      totalToSweep: Math.max(0, totalToSweep),
      sweepAmounts: zeroSweep,
      warnings: [],
      projection: {
        preSweepCumulativeCash,
        postSweepCumulativeCash: { ...preSweepCumulativeCash },
      },
    };
  }

  // Suffix-min of pre-sweep cumulative cash. Used to bound how much we can sweep
  // by the time we leave month m without breaching the floor in any later month.
  const suffixMin = new Array(N);
  for (let i = N - 1; i >= 0; i -= 1) {
    const here = preSweepCumulativeCash[safeMonths[i]];
    suffixMin[i] = i === N - 1 ? here : Math.min(here, suffixMin[i + 1]);
  }

  const evenTarget = totalToSweep / N;
  const sweepAmounts = {};
  const warnings = [];
  let carry = 0;
  let cumulativeSwept = 0;

  for (let i = 0; i < N; i += 1) {
    const m = safeMonths[i];
    const desired = evenTarget + carry;

    const headroom = Math.max(0, suffixMin[i] - floor - cumulativeSwept);

    const inc = toAmountNumber(incomeTotals?.[m]);
    const explicit = toAmountNumber(savingTotals?.[m]);
    // Realism cap only binds when there's income to save from; zero-income
    // months are funded by prior-month carryover and aren't subject to a rate.
    const realismCap = inc > 0
      ? Math.max(0, rateCap * inc - explicit)
      : Number.POSITIVE_INFINITY;

    const safeAmount = Math.min(desired, headroom);
    let sweep = Math.min(safeAmount, realismCap);
    if (sweep < safeAmount - 1e-6) {
      warnings.push({
        type: "rate_cap_exceeded",
        month: m,
        suggested: Number(safeAmount.toFixed(2)),
        capped: Number(realismCap.toFixed(2)),
        rate: rateCap,
      });
    }
    sweep = Math.max(0, sweep);
    sweepAmounts[m] = sweep;
    carry = desired - sweep;
    cumulativeSwept += sweep;
  }

  const remainder = totalToSweep - cumulativeSwept;
  if (remainder > 0.01) {
    warnings.push({
      type: "uneven_sweep",
      planned: Number(totalToSweep.toFixed(2)),
      actual: Number(cumulativeSwept.toFixed(2)),
      remainder: Number(remainder.toFixed(2)),
    });
  }

  const postSweepCumulativeCash = {};
  let runCash = ob;
  let runSwept = 0;
  for (const m of safeMonths) {
    runCash +=
      toAmountNumber(incomeTotals?.[m]) -
      toAmountNumber(savingTotals?.[m]) -
      toAmountNumber(expenseTotals?.[m]);
    runSwept += sweepAmounts[m] || 0;
    postSweepCumulativeCash[m] = runCash - runSwept;
  }

  return {
    feasibility: {
      status: "balanced",
      shortfall: 0,
      deficitMonth: null,
      deficitAmount: 0,
      message: "",
    },
    totalToSweep,
    sweepAmounts,
    warnings,
    projection: { preSweepCumulativeCash, postSweepCumulativeCash },
  };
};

/**
 * @deprecated since 2026-05 — kept as a thin shim over `analyzePlanSavings` for
 * existing callers/tests. New code should use `analyzePlanSavings` and read
 * `feasibility` + `sweepAmounts` + `warnings`.
 */
export const buildPositiveSavingsSweepAmounts = (input = {}) => {
  const { sweepAmounts } = analyzePlanSavings(input);
  return sweepAmounts;
};

export const hasPositiveSavingsSweep = (sweepAmounts = {}) =>
  Object.values(sweepAmounts || {}).some((value) => toAmountNumber(value) > 0);

export const hasPositivePlannedAmounts = ({
  months = [],
  incomeCategories = [],
  savingCategories = [],
  expenseCategories = [],
} = {}) => {
  const categoryGroups = [incomeCategories, savingCategories, expenseCategories];
  return categoryGroups.some((categories) =>
    (categories || []).some((category) =>
      (months || []).some((month) => toAmountNumber(category?.amounts?.[month]) > 0)
    )
  );
};

const toMoneyString = (value) => toAmountNumber(value).toFixed(2);

/** Returns the last month with actual data — the current month for the current
 *  plan year, which means its actuals are month-TO-DATE, not a full month.
 *  Returns null when the plan year is entirely in the future (all forecast).
 *
 *  `now` is injectable for testing; production callers pass nothing.
 */
export const getPlanCutoffMonth = (planYear, now = new Date()) => {
  const nowYear = now.getFullYear();
  if (planYear > nowYear) return null;
  if (planYear < nowYear) return ALL_PLAN_MONTHS[11]; // "Dec" — plan fully in past
  return ALL_PLAN_MONTHS[now.getMonth()];             // current month abbreviation
};

/** Returns the month the forecast line is anchored to.
 *
 *  The forecast is anchored on the CURRENT month (same month as
 *  `getPlanCutoffMonth`) so the forecast is real-time — it starts now, not one
 *  month behind. `buildForecastCumulativeBalance` pins the anchor month's
 *  forecast value to the actual month-to-date balance (so the forecast line
 *  connects to the actual line with no gap/jump) and only applies full plan
 *  projections starting the FOLLOWING month.
 *
 *  Returns null only when the plan year is entirely in the future — no actuals
 *  exist yet, so the whole forecast runs from the opening balance.
 */
export const getForecastAnchorMonth = (planYear, now = new Date()) => {
  const nowYear = now.getFullYear();
  if (planYear > nowYear) return null;
  if (planYear < nowYear) return ALL_PLAN_MONTHS[11]; // whole plan year completed
  return ALL_PLAN_MONTHS[now.getMonth()];             // current month — real-time anchor
};

/** Cumulative actual balance: anchor to historical scope balance at plan start,
 *  forward-sum cashflow.
 *
 *  `startingBalance` is the real scope balance at the first day of the first
 *  plan month — derived by the backend `getAccountBalancesAtDate` query that
 *  honors each account's openingBalance and walks back from current balance.
 *
 *  Forward-sums cashflow (income - savings - expenses) from that anchor through
 *  each plan month up to cutoffMonthAbbr.
 *
 *  monthlyActuals: { [month_abbr]: { income, expenses, savings } }
 *
 *  Note: for the current plan year the last returned month is month-TO-DATE, not
 *  a complete month. Do not anchor the forecast on it — see getForecastAnchorMonth.
 *
 *  Returns {} when cutoffMonthAbbr is null or startingBalance is null.
 */
export const buildActualCumulativeBalance = ({
  months = [],
  startingBalance,
  monthlyActuals = {},
  cutoffMonthAbbr,
}) => {
  if (!cutoffMonthAbbr || startingBalance == null) return {};

  const cutoffIdx = ALL_PLAN_MONTHS.indexOf(cutoffMonthAbbr);
  let running = toAmountNumber(startingBalance);
  const result = {};
  for (const month of months) {
    if (ALL_PLAN_MONTHS.indexOf(month) > cutoffIdx) continue;
    const { income = 0, expenses = 0, savings = 0 } = monthlyActuals[month] || {};
    running += toAmountNumber(income) - toAmountNumber(savings) - toAmountNumber(expenses);
    result[month] = running;
  }
  return result;
};

/** Cumulative forecast balance.
 *  When the anchor month is null: full forecast from openingBalance using plan projections.
 *  Otherwise: pins the anchor month to the actual balance there, then applies plan
 *  projections for every subsequent month. Months before the anchor are omitted.
 *
 *  Pass `anchorMonthAbbr` = the CURRENT month (see getForecastAnchorMonth) so the
 *  forecast is real-time. The anchor month's forecast value is pinned exactly to
 *  the actual month-to-date balance there — no proration/blending — so the
 *  forecast line connects to the actual line with no visible gap; full plan
 *  projections only apply starting the month after the anchor. `cutoffMonthAbbr`
 *  is still accepted as a legacy alias for `anchorMonthAbbr`.
 */
export const buildForecastCumulativeBalance = ({
  months = [],
  openingBalance = 0,
  actualCumulativeTotals = {},
  incomeTotals = {},
  savingTotals = {},
  expenseTotals = {},
  anchorMonthAbbr,
  cutoffMonthAbbr,
}) => {
  const result = {};
  const anchorMonth = anchorMonthAbbr !== undefined ? anchorMonthAbbr : cutoffMonthAbbr;

  if (!anchorMonth) {
    let running = toAmountNumber(openingBalance);
    for (const month of months) {
      running += toAmountNumber(incomeTotals[month]);
      running -= toAmountNumber(savingTotals[month]);
      running -= toAmountNumber(expenseTotals[month]);
      result[month] = running;
    }
    return result;
  }

  const anchorIdx = ALL_PLAN_MONTHS.indexOf(anchorMonth);
  const junctionBalance =
    actualCumulativeTotals[anchorMonth] !== undefined
      ? toAmountNumber(actualCumulativeTotals[anchorMonth])
      : toAmountNumber(openingBalance);
  let running = junctionBalance;

  for (const month of months) {
    const monthIdx = ALL_PLAN_MONTHS.indexOf(month);
    if (monthIdx < anchorIdx) continue;
    if (monthIdx === anchorIdx) {
      result[month] = junctionBalance;
      continue;
    }
    running += toAmountNumber(incomeTotals[month]);
    running -= toAmountNumber(savingTotals[month]);
    running -= toAmountNumber(expenseTotals[month]);
    result[month] = running;
  }

  return result;
};

export const applySavingsSweepToCategories = ({
  savingCategories = [],
  months = [],
  sweepAmounts = {},
  category,
} = {}) => {
  const categoryId = category?.id ? String(category.id) : "";
  if (!categoryId || !hasPositiveSavingsSweep(sweepAmounts)) {
    return savingCategories || [];
  }

  const categoryName = String(category?.name || category?.label || "Savings");
  const existingIndex = (savingCategories || []).findIndex(
    (item) => String(item?.id || "") === categoryId
  );

  if (existingIndex >= 0) {
    return (savingCategories || []).map((item, index) => {
      if (index !== existingIndex) return item;

      const amounts = { ...(item?.amounts || {}) };
      months.forEach((month) => {
        const sweep = toAmountNumber(sweepAmounts?.[month]);
        if (sweep <= 0) return;
        amounts[month] = toMoneyString(toAmountNumber(amounts?.[month]) + sweep);
      });

      return {
        ...item,
        name: item?.name || categoryName,
        label: item?.label || item?.name || categoryName,
        amounts,
      };
    });
  }

  return [
    ...(savingCategories || []),
    {
      id: categoryId,
      name: categoryName,
      label: categoryName,
      amounts: Object.fromEntries(
        months.map((month) => [month, toMoneyString(sweepAmounts?.[month])])
      ),
    },
  ];
};
